"use client";

import React from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { useTracking } from "@/components/TrackingProvider";

/**
 * Floating customer-service button on a booking site. Today it opens a WhatsApp chat with the owner; it is the single
 * place to swap in the in-app realtime chat later (keep the props small: who to reach, what to say first).
 */
export function SiteSupportButton({ whatsapp, siteName }: { whatsapp: { number: string; message: string | null } | null; siteName: string }) {
  const { t } = useI18n();
  const { track } = useTracking();
  // The server stores digits only; check again before putting it into a link.
  if (!whatsapp || !/^\d{8,15}$/.test(whatsapp.number)) return null;
  const text = whatsapp.message || t("publicsite.support.defaultMessage", { name: siteName });
  const href = `https://wa.me/${whatsapp.number}?text=${encodeURIComponent(text)}`;
  return (
    <a
      className="ps-fab"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("publicsite.support.whatsapp")}
      onClick={() => track("generate_lead", { lead_type: "whatsapp" })}
    >
      <svg width="26" height="26" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M16.04 3C9.4 3 4.02 8.37 4.02 15.01c0 2.12.55 4.19 1.6 6.01L4 28l7.17-1.88a12 12 0 0 0 4.86 1.02h.01c6.63 0 12.02-5.38 12.02-12.02A11.94 11.94 0 0 0 16.04 3zm0 21.95h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.93 9.93 0 0 1-1.52-5.3c0-5.49 4.47-9.96 9.97-9.96 2.66 0 5.16 1.04 7.04 2.92a9.9 9.9 0 0 1 2.91 7.05c0 5.5-4.47 9.97-9.97 9.97zm5.46-7.46c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
      </svg>
      <span className="ps-fab-label">{t("publicsite.support.label")}</span>
    </a>
  );
}
